import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FiTrendingUp, FiUsers, FiZap, FiDollarSign, FiActivity, FiTarget,
  FiRefreshCw, FiAlertCircle, FiBarChart2, FiAward, FiHeart,
} from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

/**
 * Growth Metrics — Terminal V3 refonte.
 *
 * All KPIs the Growth Plan calls out live here:
 *   §1.3 Day-7 retention by weekly cohort (this is THE metric that
 *        gates any paid ads spend)
 *   §2.  Free→Paid ratio, MRR, trialing users
 *   §3.  Signups by week, virality (K-factor), top sharers
 *
 * D7 cohort data comes from get_d7_cohort_retention(weeks int) — new
 * RPC in 20260722140000. Cohorts still inside their D7 window render
 * dimmed so the panel doesn't lie about incomplete data.
 */

interface Metrics {
  generated_at: string;
  total_users: number;
  new_7d: number; new_30d: number;
  analyses_total: number; analyses_7d: number; analyses_30d: number;
  active_7d: number; active_30d: number; activated_users: number;
  paid_users: number; comped_users: number; free_users: number; trialing_users: number; est_mrr: number;
  plan_distribution: { plan: string; n: number }[];
  verdicts: { v: string; n: number }[];
  outcomes: { win: number; loss: number; breakeven: number; no_trade: number; avg_r: number };
  signups_by_week: { week: string; n: number }[];
  analyses_by_week: { week: string; n: number }[];
}

interface CohortRow {
  week_start: string;
  cohort_size: number;
  active_by_d7: number;
  retention_ratio: number;
  is_partial: boolean;
}

type ShareGlobalStats = { shares_total: number; shares_7d: number; clicks_total: number; signups_total: number; k_factor: number };
type LeaderRow = { user_id: string; email: string | null; share_code: string | null; shares: number; clicks: number; signups: number; top_channel: string | null };

const pct = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 100) : 0);

// Literal classes (no dynamic interpolation) so Tailwind never purges them.
const ACCENT: Record<string, { text: string; bg: string; bar: string; border: string }> = {
  cyan:    { text: 'text-cyan-400',    bg: 'bg-cyan-500/10',    bar: 'bg-cyan-400',    border: 'border-cyan-500/30' },
  emerald: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', bar: 'bg-emerald-400', border: 'border-emerald-500/30' },
  purple:  { text: 'text-purple-400',  bg: 'bg-purple-500/10',  bar: 'bg-purple-400',  border: 'border-purple-500/30' },
  amber:   { text: 'text-amber-400',   bg: 'bg-amber-500/10',   bar: 'bg-amber-400',   border: 'border-amber-500/30' },
  gray:    { text: 'text-gray-300',    bg: 'bg-white/[0.03]',   bar: 'bg-gray-400',    border: 'border-white/10' },
};

const Kpi = ({ icon: Icon, label, value, sub, color = 'cyan' }: any) => {
  const a = ACCENT[color] || ACCENT.cyan;
  return (
    <div className={`rounded-2xl border ${a.border} ${a.bg} p-4 relative overflow-hidden`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-3.5 h-3.5 ${a.text}`} />
        <span className={`font-mono text-[10px] uppercase tracking-[0.25em] ${a.text}`}>
          {label}
        </span>
      </div>
      <div className="text-3xl font-black text-white tabular-nums font-mono leading-none">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-2 font-mono">{sub}</div>}
    </div>
  );
};

const Bars = ({ data, color = 'cyan', valueKey = 'n', labelKey = 'week' }: any) => {
  const max = Math.max(1, ...data.map((d: any) => d[valueKey]));
  const a = ACCENT[color] || ACCENT.cyan;
  return (
    <div className="flex items-end gap-1.5 h-32">
      {data.length === 0 ? (
        <p className="text-xs font-mono text-gray-600 self-center w-full text-center">no data yet</p>
      ) : data.map((d: any, i: number) => (
        <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1 min-w-0">
          <span className="text-[10px] text-white tabular-nums font-mono">{d[valueKey]}</span>
          <div className={`w-full ${a.bar} rounded-sm transition-all`} style={{ height: `${Math.max(4, (d[valueKey] / max) * 96)}px` }} />
          <span className="text-[9px] text-gray-600 truncate w-full text-center font-mono">
            {d[labelKey]?.toString().slice(5)}
          </span>
        </div>
      ))}
    </div>
  );
};

const DistRow = ({ label, n, total, color }: any) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-gray-300 w-32 truncate font-mono">{label}</span>
    <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
      <div className={`h-full ${color} rounded-full`} style={{ width: `${pct(n, total)}%` }} />
    </div>
    <span className="text-xs text-gray-400 tabular-nums w-16 text-right font-mono">
      {n} <span className="text-gray-600">({pct(n, total)}%)</span>
    </span>
  </div>
);

const VERDICT_COLOR: Record<string, string> = {
  TRADE: 'bg-emerald-400', WAIT: 'bg-purple-400', NO_TRADE: 'bg-amber-400',
  PENDING: 'bg-gray-500', CONDITIONAL: 'bg-cyan-400',
};
const PLAN_COLOR: Record<string, string> = {
  free: 'bg-cyan-400', starter: 'bg-blue-400', pro_trader: 'bg-emerald-400',
  institutional_ai: 'bg-purple-400', enterprise: 'bg-amber-400',
};

// ── D7 cohort chart ─────────────────────────────────────────────────
const D7CohortChart: React.FC<{ cohorts: CohortRow[] }> = ({ cohorts }) => {
  if (cohorts.length === 0) {
    return (
      <p className="text-xs font-mono text-gray-600 py-8 text-center">
        No cohorts yet — will populate as signups age past 7 days.
      </p>
    );
  }

  // Show average across the completed (non-partial) cohorts as the top-line.
  const completed = cohorts.filter(c => !c.is_partial);
  const avgPct = completed.length > 0
    ? Math.round(
        (completed.reduce((s, c) => s + c.retention_ratio, 0) / completed.length) * 100,
      )
    : null;

  return (
    <>
      {avgPct !== null && (
        <div className="mb-4 flex items-baseline gap-3">
          <span className="text-4xl font-black font-mono text-white tabular-nums">
            {avgPct}%
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">
            avg D7 across {completed.length} completed cohorts
          </span>
        </div>
      )}
      <div className="space-y-2">
        {cohorts.map((c, i) => {
          const p = Math.round(c.retention_ratio * 100);
          const barColor = p >= 40 ? 'bg-emerald-400'
                        : p >= 20 ? 'bg-cyan-400'
                        : p >= 10 ? 'bg-amber-400'
                        : 'bg-red-400';
          return (
            <div key={i} className={`flex items-center gap-3 ${c.is_partial ? 'opacity-40' : ''}`}>
              <span className="w-24 text-[11px] font-mono text-gray-400 truncate">
                {new Date(c.week_start).toLocaleDateString(undefined, { month: 'short', day: '2-digit' })}
              </span>
              <div className="flex-1 h-4 bg-white/5 rounded-full overflow-hidden border border-white/5">
                <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(100, Math.max(1, p))}%` }} />
              </div>
              <span className="w-14 text-right text-xs font-mono tabular-nums text-white">
                {p}%
              </span>
              <span className="w-24 text-right text-[10px] font-mono tabular-nums text-gray-500">
                {c.active_by_d7} / {c.cohort_size}
                {c.is_partial && <span className="ml-1 text-amber-400">◐</span>}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-gray-600">
        ◐ = partial cohort (still inside its D7 window)
      </div>
    </>
  );
};

const Section: React.FC<{ label: string; heading: string; children: React.ReactNode }> = ({ label, heading, children }) => (
  <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-1">
      ▸ {label}
    </div>
    <h3 className="text-white font-black mb-4">{heading}</h3>
    {children}
  </div>
);

const GrowthMetricsPanel: React.FC = () => {
  const [m, setM] = useState<Metrics | null>(null);
  const [globalStats, setGlobalStats] = useState<ShareGlobalStats | null>(null);
  const [leaders, setLeaders] = useState<LeaderRow[]>([]);
  const [cohorts, setCohorts] = useState<CohortRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [
        { data, error: e },
        { data: gs },
        { data: lb },
        { data: cohortRes },
      ] = await Promise.all([
        supabase.rpc('get_growth_metrics'),
        supabase.rpc('get_share_global_stats'),
        supabase.rpc('get_share_leaderboard', { p_limit: 50 }),
        supabase.rpc('get_d7_cohort_retention', { p_weeks: 8 }),
      ]);
      if (e) throw e;
      setM(data as unknown as Metrics);
      if (gs) setGlobalStats(gs as ShareGlobalStats);
      if (Array.isArray(lb)) setLeaders(lb as LeaderRow[]);
      if ((cohortRes as any)?.success) {
        setCohorts(((cohortRes as any).cohorts || []) as CohortRow[]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <FiRefreshCw className="animate-spin text-cyan-400 text-2xl" />
      </div>
    );
  }
  if (error || !m) {
    return (
      <div className="bg-red-500/5 border border-red-500/30 rounded-xl p-4 text-red-300 text-sm flex items-center gap-2 font-mono">
        <FiAlertCircle /> {error || 'No metrics'} — get_growth_metrics may not be deployed yet.
      </div>
    );
  }

  const winLoss = m.outcomes.win + m.outcomes.loss;
  const winRate = pct(m.outcomes.win, winLoss);
  const totalUsers = m.total_users || 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-2">
            ▸ Growth
          </div>
          <h2 className="text-2xl font-black text-white leading-tight">
            The three numbers investors look at.
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Acquisition · activation · retention · monetization. Real MRR, real D7 cohort retention.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-gray-200 hover:bg-white/5 text-xs font-mono"
        >
          <FiRefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={FiUsers}       label="Total users"       value={m.total_users} sub={`+${m.new_7d} · 7d  ·  +${m.new_30d} · 30d`} />
        <Kpi icon={FiActivity}    label="Active 7d / 30d"   value={`${m.active_7d} / ${m.active_30d}`} sub={`Stickiness ${pct(m.active_7d, m.active_30d)}% (WAU/MAU)`} color="emerald" />
        <Kpi icon={FiZap}         label="Activation"        value={`${pct(m.activated_users, totalUsers)}%`} sub={`${m.activated_users}/${m.total_users} ran an analysis`} color="purple" />
        <Kpi icon={FiBarChart2}   label="Analyses 7d"       value={m.analyses_7d} sub={`${m.analyses_total.toLocaleString()} all-time`} />
        <Kpi icon={FiDollarSign}  label="Real MRR"          value={`$${Number(m.est_mrr).toLocaleString()}`} sub={`${m.paid_users} paying${m.comped_users ? ` · ${m.comped_users} comped` : ''}`} color="emerald" />
        <Kpi icon={FiTarget}      label="Free → paid"       value={`${pct(m.paid_users, totalUsers)}%`} sub={`${m.paid_users} paid · ${m.comped_users} comp · ${m.free_users} free`} color="amber" />
        <Kpi icon={FiUsers}       label="On trial"          value={m.trialing_users} sub="trial users (Pro features)" color="purple" />
        <Kpi icon={FiAward}       label="Win rate"          value={`${winRate}%`} sub={`${m.outcomes.win}W / ${m.outcomes.loss}L resolved`} color="emerald" />
      </div>

      {/* D7 cohort retention — THE metric */}
      <Section label="Day-7 retention" heading="Cohort retention by signup week.">
        <D7CohortChart cohorts={cohorts} />
        <div className="mt-4 rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3">
          <p className="text-[11px] font-mono text-cyan-300 leading-relaxed">
            <span className="uppercase tracking-widest text-cyan-400">▸ Growth Plan §1.3:</span>
            &nbsp;this is the metric that gates paid ads. While the number stays near zero, don&apos;t spend $1 on Facebook or Google.
            Above 20% = product-market fit signal; scale from here.
          </p>
        </div>
      </Section>

      {/* Weekly trends */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section label="Acquisition" heading="New signups / week">
          <Bars data={m.signups_by_week} color="cyan" />
        </Section>
        <Section label="Engagement" heading="Analyses / week">
          <Bars data={m.analyses_by_week} color="purple" />
        </Section>
      </div>

      {/* Distributions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section label="Plan mix" heading="Plan distribution">
          <div className="space-y-2">
            {m.plan_distribution.map((p) => (
              <DistRow key={p.plan} label={p.plan} n={p.n} total={m.total_users} color={PLAN_COLOR[p.plan] || 'bg-gray-500'} />
            ))}
          </div>
        </Section>
        <Section label="Discipline signal" heading="Verdict mix">
          <div className="space-y-2">
            {m.verdicts.map((v) => (
              <DistRow key={v.v} label={v.v} n={v.n} total={m.analyses_total} color={VERDICT_COLOR[v.v] || 'bg-gray-500'} />
            ))}
          </div>
        </Section>
      </div>

      {/* Virality */}
      <Section label="Virality" heading="Share attribution (all-time)">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="text-2xl font-black font-mono text-white tabular-nums">{globalStats?.shares_total ?? '…'}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">Shares</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="text-2xl font-black font-mono text-white tabular-nums">{globalStats?.shares_7d ?? '…'}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">Shares 7d</div>
          </div>
          <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.05] p-3">
            <div className="text-2xl font-black font-mono text-cyan-400 tabular-nums">{globalStats?.clicks_total ?? '…'}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400/70 mt-1">Inbound clicks</div>
          </div>
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.05] p-3">
            <div className="text-2xl font-black font-mono text-emerald-400 tabular-nums">{globalStats?.signups_total ?? '…'}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400/70 mt-1">Signups</div>
          </div>
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.05] p-3">
            <div className="text-2xl font-black font-mono text-amber-400 tabular-nums">{globalStats ? `${globalStats.k_factor}%` : '…'}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-400/70 mt-1">K-factor</div>
          </div>
        </div>
        <p className="text-[11px] text-gray-600 mt-3 font-mono leading-relaxed">
          Per-user attribution live since 2026-06-09. Each share URL carries a unique <code className="text-cyan-400">?ref=&lt;code&gt;</code> tied to the sharing user — inbound clicks and signups are credited automatically.
        </p>
      </Section>

      {/* Top sharers */}
      <Section label="Leaderboard" heading={`Top sharers · ${leaders.length}`}>
        {leaders.length === 0 ? (
          <div className="text-xs font-mono text-gray-600 py-6 text-center">
            No share activity recorded yet. Stats fill in as users click Share on their analyses.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="text-left uppercase tracking-widest text-gray-500 border-b border-white/5">
                  <th className="py-2 font-medium">User</th>
                  <th className="py-2 font-medium">Ref code</th>
                  <th className="py-2 font-medium text-right">Shares</th>
                  <th className="py-2 font-medium text-right">Clicks</th>
                  <th className="py-2 font-medium text-right">Signups</th>
                  <th className="py-2 font-medium">Top channel</th>
                </tr>
              </thead>
              <tbody>
                {leaders.map((r) => (
                  <tr key={r.user_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-2 text-gray-200">{r.email || <span className="text-gray-600">—</span>}</td>
                    <td className="py-2"><code className="text-cyan-400 font-mono">{r.share_code || '—'}</code></td>
                    <td className="py-2 text-right text-white tabular-nums">{r.shares}</td>
                    <td className="py-2 text-right text-cyan-400 tabular-nums">{r.clicks}</td>
                    <td className={`py-2 text-right tabular-nums ${r.signups > 0 ? 'text-emerald-400 font-semibold' : 'text-gray-500'}`}>{r.signups}</td>
                    <td className="py-2 text-gray-400">{r.top_channel || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <p className="text-[10px] font-mono uppercase tracking-widest text-gray-600 text-right">
        Updated {new Date(m.generated_at).toLocaleString()}
      </p>
    </div>
  );
};

export default GrowthMetricsPanel;
